'use client';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomLink from '@/components/ui/common/custom-link';
import { useLocale } from '@/components/providers/context/locale-context';
import { JContainer } from '@/components/ui/layout';
import {
    buildStaticAssetPath,
    getStaticAssetUrl,
} from '@/app/lib/utils/static-asset-url';

function Arrow(
    props: Readonly<{
        disabled: boolean;
        left?: boolean;
        onClick: (e: any) => void;
    }>,
) {
    return (
        <svg
            onClick={props.onClick}
            className={clsx(
                `h-[40px] w-[40px] cursor-pointer sm:h-[48px] sm:w-[48px]`,
                { 'fill-slate-300': props.disabled },
            )}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 48 48"
        >
            <circle cx="24" cy="24" r="24" fill="#E4E0E1" />
            {props.left && (
                <path d="M17.1161 23.1161C16.628 23.6043 16.628 24.3957 17.1161 24.8839L25.0711 32.8388C25.5592 33.327 26.3507 33.327 26.8388 32.8388C27.327 32.3507 27.327 31.5592 26.8388 31.0711L19.7678 24L26.8388 16.9289C27.327 16.4408 27.327 15.6493 26.8388 15.1612C26.3507 14.673 25.5592 14.673 25.0711 15.1612L17.1161 23.1161ZM19 22.75L18 22.75L18 25.25L19 25.25L19 22.75Z" />
            )}
            {!props.left && (
                <path d="M29.8839 24.8839C30.372 24.3957 30.372 23.6043 29.8839 23.1161L21.9289 15.1612C21.4408 14.673 20.6493 14.673 20.1612 15.1612C19.673 15.6493 19.673 16.4408 20.1612 16.9289L27.2322 24L20.1612 31.0711C19.673 31.5592 19.673 32.3507 20.1612 32.8388C20.6493 33.327 21.4408 33.327 21.9289 32.8388L29.8839 24.8839ZM28 25.25L29 25.25L29 22.75L28 22.75L28 25.25Z" />
            )}
        </svg>
    );
}

function Kols() {
    const { t } = useTranslation('case');

    const kolList: {
        id: string;
        name: string;
        context: { text: string; isLink: boolean; link?: string }[];
    }[] = t('kolList', { returnObjects: true }) as {
        id: string;
        name: string;
        context: { text: string; isLink: boolean; link?: string }[];
    }[];

    const [loaded, setLoaded] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
        mode: 'snap',
        loop: false,
        rtl: false,
        slides: { perView: 'auto', spacing: 32 },
        breakpoints: {
            '(min-width: 640px)': {
                slides: {
                    perView: 'auto',
                    spacing: 48,
                },
            },
        },
        slideChanged(s) {
            setCurrentSlide(s.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    return (
        <JContainer size="full" padding="md" className="flex flex-col">
            <div
                className={clsx(
                    'j-h5 tablet:px-[45px] mt-[60px] flex w-full flex-col px-[15px] text-left text-[#06060A]',
                )}
            >
                <div>{t('answer.firstLine')}</div>
                <div>
                    <span>{t('answer.secondLine.part1')} </span>
                    <span className="text-[#6B48FF]">
                        {t('answer.secondLine.part2')}
                    </span>
                </div>
            </div>

            <div ref={sliderRef} className="keen-slider">
                {kolList.map((kol, index) => (
                    <div
                        id={'data-problems-' + kol.id}
                        key={kol.id}
                        className={clsx(`keen-slider__slide flex`, {
                            //Keen Slider 在 flex 布局下是依赖 min-width 计算的。
                            'min-w-[294px] sm:min-w-[550px]':
                                index == 0 || index == kolList.length - 1,
                            'min-w-[278px] sm:min-w-[510px]':
                                index != 0 && index != kolList.length - 1,
                        })}
                    >
                        <div
                            style={{
                                backgroundImage: `url(${getStaticAssetUrl(
                                    buildStaticAssetPath(
                                        `bg-card-${index + 1}.webp`,
                                    ),
                                )})`,
                            }}
                            className={clsx(
                                'my-[20px] flex w-full flex-col rounded-[6px] bg-cover bg-center bg-no-repeat px-[16px] py-[16px] sm:my-[60px] sm:rounded-[12px] sm:px-[32px] sm:py-[40px]',
                                {
                                    'ml-[16px] sm:ml-[40px] ': index == 0,
                                    'mr-[16px] sm:mr-[40px] ':
                                        index == kolList.length - 1,
                                },
                            )}
                        >
                            <div
                                className={clsx(
                                    'j-h5 h-[3.6em] text-left leading-[1.2em] text-[#FFFFFF] sm:h-[3em] sm:leading-[1.5em]',
                                )}
                            >
                                {kol.name}
                            </div>

                            <div
                                className={clsx(
                                    'j-t2 mt-[16px] text-left text-[#FFFFFF] sm:mt-[24px]',
                                )}
                            >
                                <p>
                                    {kol.context.map((line, index) =>
                                        line.isLink ? (
                                            <CustomLink
                                                href={line.link ?? ''}
                                                key={'link-' + index}
                                                fill="#FFFFFF"
                                            >
                                                <span
                                                    className={clsx('inline')}
                                                >
                                                    {line.text}
                                                </span>
                                            </CustomLink>
                                        ) : (
                                            <span
                                                className="inline"
                                                key={'span-' + index}
                                            >
                                                {line.text}
                                            </span>
                                        ),
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {loaded && instanceRef.current && (
                <div className="flex w-full flex-row justify-end gap-[8px] pb-[20px] pr-[16px] sm:gap-[16px] sm:pb-[40px] sm:pr-[40px]">
                    <Arrow
                        left
                        onClick={(e: any) =>
                            e.stopPropagation() || instanceRef.current?.prev()
                        }
                        disabled={currentSlide === 0}
                    />

                    <Arrow
                        onClick={(e: any) =>
                            e.stopPropagation() || instanceRef.current?.next()
                        }
                        disabled={
                            currentSlide ===
                            instanceRef.current.track.details.slides.length - 1
                        }
                    />
                </div>
            )}
        </JContainer>
    );
}

export default Kols;
